/**
 * Central casing policy for in-app notification banner titles.
 *
 * Change {@link BANNER_TITLE_CASE_STYLE} to restyle every banner title
 * that flows through {@link formatBannerTitle} (push / toast banners).
 */

export const BANNER_TITLE_CASE = Object.freeze({
  /** First letter uppercase; remaining letters lowercase. e.g. "Successfully subscribed" */
  SENTENCE: 'sentence',
  /** Each word capitalized. e.g. "Successfully Subscribed" */
  TITLE: 'title',
  /** All letters uppercase. e.g. "SUCCESSFULLY SUBSCRIBED" */
  UPPER: 'upper',
});

/**
 * App-wide banner title casing. Flip this single value when product wants a new style.
 * @type {(typeof BANNER_TITLE_CASE)[keyof typeof BANNER_TITLE_CASE]}
 */
export const BANNER_TITLE_CASE_STYLE = BANNER_TITLE_CASE.SENTENCE;

/** Private-use placeholders so @handles and emoji keep original glyphs through casing. */
const TOKEN_PREFIX = '\uE000';
const TOKEN_SUFFIX = '\uE001';

/**
 * @username handles + emoji sequences (including ZWJ / variation selectors).
 * Kept out of casing transforms so display identity stays intact.
 */
const PRESERVE_TOKEN_RE =
  /@[A-Za-z0-9._-]+|\p{Extended_Pictographic}(?:\uFE0F|\u200D\p{Extended_Pictographic})*/gu;

const LETTER_RE = /[A-Za-zÀ-ÖØ-öø-ÿ]/;

const isTokenPlaceholder = (value) =>
  typeof value === 'string' &&
  value.startsWith(TOKEN_PREFIX) &&
  value.endsWith(TOKEN_SUFFIX);

const extractPreservedTokens = (text) => {
  const tokens = [];
  const masked = text.replace(PRESERVE_TOKEN_RE, (match) => {
    const index = tokens.length;
    tokens.push(match);
    return `${TOKEN_PREFIX}${index}${TOKEN_SUFFIX}`;
  });
  return { masked, tokens };
};

const restorePreservedTokens = (text, tokens) =>
  text.replace(
    new RegExp(`${TOKEN_PREFIX}(\\d+)${TOKEN_SUFFIX}`, 'g'),
    (_, index) => tokens[Number(index)] ?? ''
  );

/**
 * True when `index` sits inside a `${TOKEN_PREFIX}n${TOKEN_SUFFIX}` span.
 * @param {string} text
 * @param {number} index
 */
const isInsideTokenPlaceholder = (text, index) => {
  const start = text.lastIndexOf(TOKEN_PREFIX, index);
  if (start === -1) return false;
  const end = text.indexOf(TOKEN_SUFFIX, start + TOKEN_PREFIX.length);
  if (end === -1) return false;
  return index >= start && index <= end + TOKEN_SUFFIX.length - 1;
};

/**
 * @param {string} masked
 * @param {string[]} tokens
 * @returns {boolean}
 */
const hasLeadingMention = (masked, tokens) => {
  const leading = masked.match(
    new RegExp(`^(?:\\s|${TOKEN_PREFIX}\\d+${TOKEN_SUFFIX})*`)
  );
  if (!leading) return false;

  const chunk = leading[0];
  const ids = [...chunk.matchAll(new RegExp(`${TOKEN_PREFIX}(\\d+)${TOKEN_SUFFIX}`, 'g'))];
  return ids.some((match) => String(tokens[Number(match[1])] || '').startsWith('@'));
};

/**
 * Capitalize the first letter of a word; leave non-letter prefixes (e.g. punctuation) in place.
 * @param {string} word
 */
const capitalizeWord = (word) => {
  if (!word || isTokenPlaceholder(word)) return word;

  const match = word.match(LETTER_RE);
  if (!match) return word;

  const at = match.index;
  return (
    word.slice(0, at) +
    word.charAt(at).toUpperCase() +
    word.slice(at + 1).toLowerCase()
  );
};

/**
 * @param {string} masked
 * @param {string[]} tokens
 */
const toSentenceCase = (masked, tokens) => {
  const lower = masked.toLowerCase();
  const skipCapitalize = hasLeadingMention(masked, tokens);

  if (skipCapitalize) {
    return lower;
  }

  for (let i = 0; i < lower.length; i += 1) {
    if (isInsideTokenPlaceholder(lower, i)) continue;
    if (!LETTER_RE.test(lower[i])) continue;

    return lower.slice(0, i) + lower.charAt(i).toUpperCase() + lower.slice(i + 1);
  }

  return lower;
};

/**
 * @param {string} masked
 */
const toTitleCase = (masked) =>
  masked
    .split(/(\s+)/)
    .map((part) => {
      if (!part || /^\s+$/.test(part) || isTokenPlaceholder(part)) return part;
      return capitalizeWord(part);
    })
    .join('');

/**
 * Formats a notification banner title to the app-wide casing style.
 * Safe for empty/null input; preserves @handles and emoji.
 *
 * @param {unknown} title - Raw banner title from API, FCM, or local copy
 * @param {(typeof BANNER_TITLE_CASE)[keyof typeof BANNER_TITLE_CASE]} [style]
 * @returns {string}
 *
 * @example
 * formatBannerTitle('SUCCESSFULLY SUBSCRIBED 🎊') // 'Successfully subscribed 🎊'
 * formatBannerTitle('tip sent 🎉', BANNER_TITLE_CASE.TITLE) // 'Tip Sent 🎉'
 */
export const formatBannerTitle = (
  title,
  style = BANNER_TITLE_CASE_STYLE
) => {
  if (title == null) return '';

  const raw = String(title).trim();
  if (!raw) return '';

  const { masked, tokens } = extractPreservedTokens(raw);

  let formatted;
  switch (style) {
    case BANNER_TITLE_CASE.UPPER:
      formatted = masked.toUpperCase();
      break;
    case BANNER_TITLE_CASE.TITLE:
      formatted = toTitleCase(masked);
      break;
    case BANNER_TITLE_CASE.SENTENCE:
    default:
      formatted = toSentenceCase(masked, tokens);
      break;
  }

  return restorePreservedTokens(formatted, tokens);
};
